import { z } from 'zod';
/**
 * Reusable environment field schemas.
 *
 * Environment variables arrive as strings or not at all. These helpers do the
 * coercion once, correctly, so no app hand-rolls `value === 'true'` or
 * `parseInt(value, 10)` with its own edge cases.
 */
/** Deployment stage. Distinct from NODE_ENV, which only tells us about tooling. */
export const APP_ENVIRONMENTS = ['local', 'development', 'staging', 'production'];
export const appEnvironmentSchema = z.enum(APP_ENVIRONMENTS);
export const nodeEnvSchema = z.enum(['development', 'test', 'production']);
export const logLevelSchema = z.enum(['trace', 'debug', 'info', 'warn', 'error', 'fatal']);
/** A required, non-blank string. Guards against `FOO=` in a .env file. */
export const nonEmptyString = (description) => z.string().trim().min(1, `${description} must not be empty`);
/** A secret. Same as `nonEmptyString` but flagged for redaction tooling. */
export const secretString = (description, minLength = 16) => z.string().trim().min(minLength, `${description} looks too short to be a real credential`);
export const urlString = (description) => z.url({ message: `${description} must be a valid absolute URL` });
/** TCP port, accepting the string form platforms like Railway inject. */
export const portSchema = z.coerce
    .number()
    .int('port must be an integer')
    .min(1, 'port must be between 1 and 65535')
    .max(65535, 'port must be between 1 and 65535');
/** Accepts `true/false`, `1/0`, `yes/no`, case-insensitively. */
export const booleanString = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.enum(['true', 'false', '1', '0', 'yes', 'no']))
    .transform((value) => value === 'true' || value === '1' || value === 'yes');
/**
 * Comma-separated list, trimmed, with empty entries dropped.
 *
 * Absence is handled inside the schema rather than with `.default()`. A default
 * on a transforming schema applies to the *output* side, so `.default('')`
 * would hand callers a bare string where they expect an array — a type-safe
 * looking footgun. Making the input optional keeps the output shape honest.
 */
export const csvList = z
    .string()
    .optional()
    .transform((value) => (value ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0))
    .pipe(z.array(z.string()));
/** A positive duration in milliseconds. */
export const durationMs = z.coerce.number().int().positive();
//# sourceMappingURL=schemas.js.map